import React, { useEffect, useState } from 'react';
import { Dayjs } from 'dayjs';
import { Divider } from 'antd';
import type { ParsedUrlQuery } from 'querystring';
import { TablePaginationConfig } from 'antd/es/table';
import { useCategories } from 'Inventory/hooks/categories';
import { useSalesReport } from 'Reports/hooks/sales-reports';
import { mapSalesReportsRecords } from 'Reports/utils';
import { SalesReportTableParams } from 'utils/apiClient/services/reports/types';
import SalesReportTable from './views/SalesReportTable';
import SalesReportHeader from './views/SalesReportHeader';

type SalesReportProps = {
  dateFilter: Dayjs[];
  currency: string;
  query: ParsedUrlQuery;
};
const SalesReport: React.FunctionComponent<SalesReportProps> = ({
  dateFilter,
  currency,
  query,
}) => {
  const [startDate, endDate] = dateFilter;

  const [tableParams, setTableParams] = useState<SalesReportTableParams>({
    filters: {
      start: startDate,
      end: endDate,
    },
    sorter: { columnKey: 'revenue_gross', order: 'descend' },
    pagination: {
      current: 1,
      pageSize: 10,
      showSizeChanger: true,
    },
  });
  useEffect(() => {
    setTableParams((prevTableParams) => ({
      ...prevTableParams,
      filters: {
        start: startDate,
        end: endDate,
      },
    }));
  }, [startDate, endDate]);

  const categoriesQuery = useCategories({
    pagination: { current: 1, pageSize: 200 },
    sorter: { columnKey: 'name', order: 'ascend' },
  });
  const salesQuery = useSalesReport({ tableParams, enabled: query._ === '2' });

  const handleCategoriesFilterChange = (catList: number[]) =>
    setTableParams((state: SalesReportTableParams) => ({
      ...state,
      filters: {
        ...state.filters,
        categories: catList,
      },
    }));

  const handleTableChange = ({
    filters,
    ...params
  }: SalesReportTableParams) => {
    setTableParams((state: SalesReportTableParams) => ({
      filters: {
        ...state.filters,
        ...filters,
      },
      sorter: params.sorter,
      pagination: {
        ...(params.pagination as TablePaginationConfig),
      },
    }));
  };

  return (
    <>
      <SalesReportHeader
        handleCategoriesFilterChange={handleCategoriesFilterChange}
        categories={
          categoriesQuery.data !== undefined ? categoriesQuery.data.data : []
        }
      />
      <Divider css="margin: 0" />
      <div css="padding: 24px; overflow:auto">
        <SalesReportTable
          data={
            salesQuery.data !== undefined
              ? mapSalesReportsRecords(salesQuery.data.data)
              : []
          }
          currency={currency}
          tableParams={{
            ...tableParams,
            ...(salesQuery.data !== undefined && {
              pagination:
                salesQuery.data.total < 10
                  ? false
                  : {
                      ...(tableParams.pagination as TablePaginationConfig),
                      total: salesQuery?.data.total,
                    },
            }),
          }}
          loading={salesQuery.isLoading}
          handleTableChange={handleTableChange}
        />
      </div>
    </>
  );
};

export default SalesReport;
